import { AgentProfile } from "@closerplatform/spinner-openapi";
import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Avatar } from "@fidget/common/components/avatar/avatar";
import { ClickAwayListener } from "@fidget/common/components/form/clickAwayListener";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n } from "@fidget/common/i18n";
import { logger } from "@fidget/common/utils";
import { useStore } from "../../../models";
import { IRoom } from "../../../models/room/Room";
import { ITagGroup } from "../../../models/tagGroups/TagGroupStore";
import { ITag } from "../../../models/tags/TagsStore";
import { AdviserPicker } from "../../forms/adviserPicker/adviserPicker";
import { TagGroupPicker } from "../../forms/tagPicker/tagGroupPicker";
import { TagPicker } from "../../forms/tagPicker/tagPicker";
import styles from "./assigneeControl.css";
import {
  AssigneeControlType,
  AssigneeControlTypePicker,
  mapAssigneeCntrolTypeToString
} from "./assigneeControlTypePicker/assigneeControlTypePicker";
import { useAgentServices } from "../../../models/agentServices";
import { HttpStatusCode } from "@fidget/common/utils/httpStatusCodes";

interface Props {
  room: IRoom;
}

enum PickingState {
  Idle,
  Tag,
  GroupTag,
  Advisers
}

export const AssigneeControl = observer(({ room }: Props) => {
  const { spinnerService } = useAgentServices();
  const {
    currentUserStore,
    uiStore,
    tagGroupsStore,
    agentStore,
    tagsStore,
    roomStore,
    tagGroupRulesStore
  } = useStore();
  const [agents, setAgents] = useState<Array<AgentProfile>>();
  const [pickingState, setPickingState] = useState<PickingState>(PickingState.Idle);
  const [isPickingAssignmentType, setIsPickingAssignmentType] = useState<boolean>(false);
  const [currentAssignmentType, setCurrentAssignmentType] = useState<
    AssigneeControlType | undefined
  >();
  const roomTagGroupId = roomStore.currentRoom?.owner.tagGroupId;
  const transferRulesEnabled = tagGroupRulesStore.enabled;
  const tagGroupTransferRule =
    transferRulesEnabled && roomTagGroupId && tagGroupRulesStore.getByTagGroup(roomTagGroupId);

  const enabledTransferGroups = tagGroupTransferRule
    ? tagGroupsStore
        .getFromIds(tagGroupTransferRule.targetTagGroupIds)
        .filter((group) => group.enabled)
    : [];

  useEffect(() => {
    if (roomTagGroupId) {
      Promise.all([
        tagGroupRulesStore.loadRule(roomTagGroupId),
        tagGroupRulesStore.loadParameters()
      ]);
    }
  }, [room]);

  const handleSetCurrentAssignmentType = (value: boolean) => {
    if (uiStore.canUseConversationAssign()) {
      setIsPickingAssignmentType(value);
    }
  };

  const resetStateToTypePicking = () => {
    setCurrentAssignmentType(undefined);
    setPickingState(PickingState.Idle);
    setIsPickingAssignmentType(true);
  };

  const resetStateAfterFailedAssignment = () => {
    setPickingState(PickingState.Idle);
    handleSetCurrentAssignmentType(false);
  };

  const resetStateAfterSuccessfulAssignment = () => {
    roomStore.resetCurrentRoom();
  };

  const handleAssignmentTypeChange = (value: AssigneeControlType | null) => {
    setCurrentAssignmentType(value || undefined);

    if (value === AssigneeControlType.ToAdviser) {
      showAssigneeDropdown();
    } else if (value === AssigneeControlType.ToQueue) {
      setPickingState(PickingState.Tag);
    } else if (value === AssigneeControlType.ToGroupQueue) {
      setPickingState(PickingState.GroupTag);
    } else if (value === AssigneeControlType.ToMe && currentUserStore.id) {
      handleAssigneePicked(currentUserStore.id);
    }
  };

  const handleOutsideClick = () => {
    setPickingState(PickingState.Idle);
    handleSetCurrentAssignmentType(false);
    setCurrentAssignmentType(undefined);
  };

  const showAssigneeDropdown = async () => {
    try {
      await agentStore.loadAgentProfiles();
      const allAssignees = agentStore.agentProfiles;

      const tranferAssignees = tagGroupTransferRule
        ? allAssignees.filter((agent) => {
            const agentTagGroups = agentStore.getAllAgentTagGroups(agent.id);
            return tagGroupTransferRule.targetTagGroupIds.some((agentTagGroupId) =>
              agentTagGroups.includes(agentTagGroupId)
            );
          })
        : [];

      const agentByTransferRules = transferRulesEnabled ? tranferAssignees : allAssignees;

      setAgents(agentByTransferRules);
      setPickingState(PickingState.Advisers);
    } catch (error) {
      logger.error({ message: "Couldn't show assignee dropdown", error });
    }
  };

  const handleAssigneePicked = async (id: string) => {
    try {
      await room.assignAgent(id);
      resetStateAfterSuccessfulAssignment();
    } catch (e) {
      logger.error({ message: "Couldn't set assignee", error: e });
      uiStore.addGenericErrorToast();
      resetStateAfterFailedAssignment();
    } finally {
      setAgents(undefined);
    }
  };

  const handleTagPicked = async (tag: ITag) => {
    try {
      await room.owner.update({ tags: [tag.name] }, false);
      await spinnerService.autoAssignConversation(room.id, { tag: tag.name });
      resetStateAfterSuccessfulAssignment();
    } catch (e) {
      if ((e as Response).status === HttpStatusCode.CONFLICT) {
        resetStateAfterSuccessfulAssignment();
      } else {
        logger.error({ message: `Couldn't assign conversation to tag ${tag}`, error: e });
        uiStore.addGenericErrorToast();
        resetStateAfterFailedAssignment();
      }
    }
  };

  const handleTagGroupPicked = async (tagGroup: ITagGroup) => {
    try {
      await room.owner.update({ tags: [], tagGroupId: tagGroup.id }, false);
      await spinnerService.autoAssignConversation(room.id, { tagGroupId: tagGroup.id });
      resetStateAfterSuccessfulAssignment();
    } catch (e) {
      if ((e as Response).status === HttpStatusCode.CONFLICT) {
        resetStateAfterSuccessfulAssignment();
      } else {
        logger.error({
          message: `Couldn't assign conversation to tag group ${tagGroup}`,
          error: e
        });
        uiStore.addGenericErrorToast();
        resetStateAfterFailedAssignment();
      }
    }
  };

  const renderAgentDropdown = (values: Array<AgentProfile>) => {
    return (
      <AdviserPicker
        initialIsOpen
        className={styles["input-width"]}
        placeholder={mapAssigneeCntrolTypeToString(AssigneeControlType.ToAdviser)}
        onStateChange={(_, { isOpen }) => !isOpen && resetStateToTypePicking()}
        values={values}
        onChange={({ id }) => handleAssigneePicked(id)}
      />
    );
  };

  const renderTagsDropdown = () => {
    return (
      <TagPicker
        initialIsOpen
        className={styles["input-width"]}
        onChange={handleTagPicked}
        values={tagsStore.assignableTags}
        placeholder={mapAssigneeCntrolTypeToString(AssigneeControlType.ToQueue)}
        onStateChange={(_, { isOpen }) => !isOpen && resetStateToTypePicking()}
      />
    );
  };

  const renderTagGroupsDropdown = () => {
    return (
      <TagGroupPicker
        initialIsOpen
        className={styles["input-width"]}
        onChange={handleTagGroupPicked}
        values={transferRulesEnabled ? enabledTransferGroups : tagGroupsStore.enabledGroups}
        placeholder={mapAssigneeCntrolTypeToString(AssigneeControlType.ToGroupQueue)}
        onStateChange={(_, { isOpen }) => !isOpen && resetStateToTypePicking()}
      />
    );
  };

  const renderUnassignedState = () => (
    <div
      className={classNames(styles["assignee-control"], styles.unassigned)}
      onClick={() => handleSetCurrentAssignmentType(true)}
    >
      <L10n id={"conversation.assignee.unassigned"} />
    </div>
  );

  const renderAssignedState = () => {
    return (
      <div className={styles["assign-to-container"]}>
        <span className={styles["assigned-to"]}>
          <L10n id={"conversation.assignee.assignedTo"} />
        </span>
        <div
          className={styles["assignee-control"]}
          onClick={() => handleSetCurrentAssignmentType(true)}
        >
          <div className={styles["assignee-avatar"]}>
            <Avatar user={room.assignee} />
          </div>
          <span>
            {room.isAssignedToMe ? (
              <L10n id={"conversation.assignee.you"} />
            ) : (
              <span>{room.assignee!.displayedName}</span>
            )}
          </span>
        </div>
      </div>
    );
  };

  const renderTypePicker = () => {
    return (
      <AssigneeControlTypePicker
        onChange={handleAssignmentTypeChange}
        currentType={currentAssignmentType}
        showAssignToMe={!room.isAssignedToMe}
        showAssignToTag={!!tagsStore.assignableTags.length}
        showAssignToTagGroup={!!tagGroupsStore.enabledGroups.length}
        initialIsOpen
        onInputClick={handleOutsideClick}
      />
    );
  };

  const renderPristine = () => {
    return (
      <TooltipComponent
        positionPreset={"bottom"}
        text={<L10n id={"conversation.assignee.tooltip"} />}
        tooltipShow={pickingState !== PickingState.Idle}
        maxWidth={"150%"}
      >
        {room.assignee ? renderAssignedState() : renderUnassignedState()}
      </TooltipComponent>
    );
  };

  const renderContent = () => {
    if (pickingState === PickingState.Advisers && agents) {
      return renderAgentDropdown(agents);
    }

    if (pickingState === PickingState.Tag) {
      return renderTagsDropdown();
    }

    if (pickingState === PickingState.GroupTag) {
      return renderTagGroupsDropdown();
    }

    if (isPickingAssignmentType) {
      return renderTypePicker();
    }

    return renderPristine();
  };

  const render = () => {
    return (
      <ClickAwayListener onClickAway={handleOutsideClick}>
        <div className={styles["assignee-container"]}>{renderContent()}</div>
      </ClickAwayListener>
    );
  };

  return render();
});
